// ============================================
// pages/release-orders/GenerateROForm.tsx — real API create/edit
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle, XCircle, FileText } from 'lucide-react';
import { seasonsAPI, commoditiesAPI, godownsAPI, releaseOrdersAPI } from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

const empty = {
  season_id: 0, commodity_id: 0, godown_id: 0,
  ro_number: '', ro_date: new Date().toISOString().slice(0, 10), validity_days: 30,
  buyer_name: '', buyer_type: 'private', buyer_gst: '', buyer_address: '', buyer_phone: '', buyer_email: '',
  lot_number: '', quantity_mts: 0, rate_per_mt: 0,
  gst_rate: 5, advance_pct: 85, tds_rate: 0.1,
  emd_amount: 0, sd_amount: 0,
  payment_mode: 'neft', bank_name: '', utr_number: '', payment_date: '',
  payment_received: 0,
  source_godown_ids: [] as number[],
  source_unloading_bill_ids: [] as number[],
  source_notes: '',
  remarks: '',
};

export const GenerateROForm: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreate = !idParam || idParam === 'new' || idParam === 'generate';
  const recordId = isCreate ? null : parseInt(idParam);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [godowns, setGodowns] = useState<any[]>([]);
  const [form, setForm] = useState<any>(empty);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isLocked = ['submitted', 'dm_verified', 'manager_approved', 'closed'].includes(status);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, g] = await Promise.all([seasonsAPI.list(), commoditiesAPI.list(), godownsAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setCommodities(c.data);
        setGodowns(g.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (isCreate) {
          setForm((f: any) => ({ ...f, season_id: active?.id || 0 }));
        } else if (recordId && active) {
          const res = await releaseOrdersAPI.get(active.id, recordId);
          const raw = res.data as any;
          setForm({ ...empty, ...raw });
          setStatus(raw.status || 'draft');
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const computed = useMemo(() => {
    const qty = num(form.quantity_mts);
    const rate = num(form.rate_per_mt);
    const roValue = +(qty * rate).toFixed(2);
    const gstAmount = +((roValue * num(form.gst_rate)) / 100).toFixed(2);
    const totalValue = roValue + gstAmount;
    const advance = +((totalValue * num(form.advance_pct)) / 100).toFixed(2);
    const balance = totalValue - advance;
    const tds = +((totalValue * num(form.tds_rate)) / 100).toFixed(2);
    const netPayable = totalValue - tds;
    const balanceReceivable = Math.max(totalValue - num(form.payment_received), 0);
    return { roValue, gstAmount, totalValue, advance, balance, tds, netPayable, balanceReceivable };
  }, [form.quantity_mts, form.rate_per_mt, form.gst_rate, form.advance_pct, form.tds_rate, form.payment_received]);

  const handleChange = (field: string, value: any) => setForm((f: any) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.season_id || !form.commodity_id || !form.godown_id || !form.ro_number || !form.buyer_name || !form.quantity_mts || !form.rate_per_mt) {
      setMessage({ type: 'error', text: 'All required fields must be filled' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const seasonId = form.season_id;
      if (isCreate) {
        const res = await releaseOrdersAPI.create(seasonId, form);
        const newId = (res.data as any)?.id;
        setMessage({ type: 'success', text: 'Saved' });
        if (newId) navigate(`/release-orders/edit/${newId}`, { replace: true });
      } else if (recordId) {
        await releaseOrdersAPI.update(seasonId, recordId, form);
        setMessage({ type: 'success', text: 'Updated' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-5xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-purple-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={() => navigate('/release-orders')}
        className="mb-4 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">
        <ArrowLeft className="w-4 h-4" />Back to List
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <FileText className="w-7 h-7 text-purple-600" />
          {isCreate ? 'New Release Order' : `RO #${form.ro_number || recordId}`}
        </h1>
        <p className="text-sm text-gray-500 mt-1">Status: <span className="font-semibold uppercase">{status}</span></p>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="mb-3 text-sm font-semibold text-gray-700 border-b pb-2">RO Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Season</label>
            <select value={form.season_id} onChange={(e) => handleChange('season_id', parseInt(e.target.value))}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Commodity</label>
            <select value={form.commodity_id} onChange={(e) => handleChange('commodity_id', parseInt(e.target.value))}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Godown</label>
            <select value={form.godown_id} onChange={(e) => handleChange('godown_id', parseInt(e.target.value))}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {godowns.map((g) => (<option key={g.id} value={g.id}>{g.godown_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">RO Number</label>
            <input type="text" value={form.ro_number || ''} onChange={(e) => handleChange('ro_number', e.target.value)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">RO Date</label>
            <input type="date" value={form.ro_date || ''} onChange={(e) => handleChange('ro_date', e.target.value)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Validity (days)</label>
            <input type="number" value={form.validity_days || ''} onChange={(e) => handleChange('validity_days', parseInt(e.target.value) || 0)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Lot Number</label>
            <input type="text" value={form.lot_number || ''} onChange={(e) => handleChange('lot_number', e.target.value)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
        </div>

        <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Buyer</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Buyer Name</label>
            <input type="text" value={form.buyer_name || ''} onChange={(e) => handleChange('buyer_name', e.target.value)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Buyer Type</label>
            <select value={form.buyer_type} onChange={(e) => handleChange('buyer_type', e.target.value)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value="fci">FCI</option>
              <option value="civil_supplies">Civil Supplies</option>
              <option value="private">Private</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">GSTIN</label>
            <input type="text" value={form.buyer_gst || ''} onChange={(e) => handleChange('buyer_gst', e.target.value)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
            <input type="text" value={form.buyer_phone || ''} onChange={(e) => handleChange('buyer_phone', e.target.value)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={form.buyer_email || ''} onChange={(e) => handleChange('buyer_email', e.target.value)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
            <textarea value={form.buyer_address || ''} onChange={(e) => handleChange('buyer_address', e.target.value)}
              disabled={isLocked} rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
        </div>

        <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Quantity &amp; Pricing</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Quantity (MT)</label>
            <input type="number" step="0.001" value={form.quantity_mts || ''} onChange={(e) => handleChange('quantity_mts', parseFloat(e.target.value) || 0)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Rate / MT</label>
            <input type="number" step="0.01" value={form.rate_per_mt || ''} onChange={(e) => handleChange('rate_per_mt', parseFloat(e.target.value) || 0)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">GST %</label>
            <input type="number" step="0.01" value={form.gst_rate || ''} onChange={(e) => handleChange('gst_rate', parseFloat(e.target.value) || 0)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Advance %</label>
            <input type="number" step="0.01" value={form.advance_pct || ''} onChange={(e) => handleChange('advance_pct', parseFloat(e.target.value) || 0)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">EMD Amount</label>
            <input type="number" step="0.01" value={form.emd_amount || ''} onChange={(e) => handleChange('emd_amount', parseFloat(e.target.value) || 0)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">SD Amount</label>
            <input type="number" step="0.01" value={form.sd_amount || ''} onChange={(e) => handleChange('sd_amount', parseFloat(e.target.value) || 0)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">TDS %</label>
            <input type="number" step="0.01" value={form.tds_rate || ''} onChange={(e) => handleChange('tds_rate', parseFloat(e.target.value) || 0)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Payment Received</label>
            <input type="number" step="0.01" value={form.payment_received || ''} onChange={(e) => handleChange('payment_received', parseFloat(e.target.value) || 0)}
              disabled={isLocked} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div><p className="text-xs text-gray-500">RO Value</p><p className="text-base font-bold">{formatAmount(computed.roValue)}</p></div>
            <div><p className="text-xs text-gray-500">GST</p><p className="text-base font-bold">{formatAmount(computed.gstAmount)}</p></div>
            <div><p className="text-xs text-gray-500">Total Value</p><p className="text-base font-bold text-purple-700">{formatAmount(computed.totalValue)}</p></div>
            <div><p className="text-xs text-gray-500">Advance</p><p className="text-base font-bold">{formatAmount(computed.advance)}</p></div>
            <div><p className="text-xs text-gray-500">Balance</p><p className="text-base font-bold">{formatAmount(computed.balance)}</p></div>
            <div><p className="text-xs text-gray-500">TDS</p><p className="text-base font-bold text-red-600">{formatAmount(computed.tds)}</p></div>
            <div><p className="text-xs text-gray-500">Net Payable</p><p className="text-base font-bold">{formatAmount(computed.netPayable)}</p></div>
            <div><p className="text-xs text-gray-500">Balance Receivable</p><p className="text-base font-bold text-orange-600">{formatAmount(computed.balanceReceivable)}</p></div>
          </div>
        </div>

        <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Source Traceability (audit trail)</h3>
        <p className="text-xs text-gray-500 mb-3">
          Which godowns / unloading bills did this released stock come from? Stored as JSON arrays.
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Source Notes</label>
          <textarea value={form.source_notes || ''} onChange={(e) => handleChange('source_notes', e.target.value)}
            disabled={isLocked} rows={2}
            placeholder="e.g. Stock released from CWC Adilabad (500 MT) + TGSWC Nirmal (500 MT). Invoices INV/001 and INV/015."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          <p className="text-[10px] text-gray-400 mt-1">
            Free text for now. Future: pick godown_ids and unloading_bill_ids from a multi-select.
          </p>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
          <textarea value={form.remarks || ''} onChange={(e) => handleChange('remarks', e.target.value)}
            disabled={isLocked} rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={handleSave} disabled={saving || isLocked}
            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isCreate ? 'Save Draft' : 'Update'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateROForm;
